import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, AreaChart, Area, ReferenceLine } from 'recharts';
import SafeResponsiveContainer from './ui/SafeResponsiveContainer';
import { Activity, TrendingUp, Zap, Timer, ArrowRight, AlertTriangle, Info } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function SprintAnalytics({ childId, childName }) {
  const [sprintData, setSprintData] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (childId) {
      fetchSprintData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const fetchSprintData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch both raw tests and analytics with benchmarks
      const [testsResponse, analyticsResponse] = await Promise.all([
        axios.get(`${API}/children/${childId}/athletic-tests`, { headers }),
        axios.get(`${API}/analytics/sprint/${childId}`, { headers }).catch(() => null)
      ]);
      
      // Filter for sprint-related tests
      const sprints = testsResponse.data.filter(test => 
        test.test_name?.toLowerCase().includes('sprint') ||
        test.test_name?.toLowerCase().includes('speed')
      );
      
      setSprintData(sprints);
      if (analyticsResponse?.data) {
        setAnalyticsData(analyticsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching sprint data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group sprint data by test session (date)
  const sprintSessions = useMemo(() => {
    const sessions = {};
    sprintData.forEach(test => {
      const dateKey = new Date(test.test_date).toISOString().split('T')[0];
      if (!sessions[dateKey]) {
        sessions[dateKey] = {
          date: dateKey,
          tests: {}
        };
      }
      sessions[dateKey].tests[test.test_name] = test.result_value;
    });
    return Object.values(sessions).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [sprintData]);

  // Calculate speed vs distance data for a 30m sprint
  const speedDistanceData = useMemo(() => {
    const latestSession = sprintSessions[sprintSessions.length - 1];
    if (!latestSession) return [];

    const data = [];
    const splits = [
      { distance: 10, timeKey: 'Standing Sprint 10m', splitKey: 'Sprint 10m Split' },
      { distance: 20, timeKey: 'Standing Sprint 20m', splitKey: 'Sprint 20m Split' },
      { distance: 30, timeKey: 'Standing Sprint 30m', splitKey: 'Sprint 30m Split' }
    ];

    let prevTime = 0;
    splits.forEach(({ distance, timeKey, splitKey }) => {
      const time = latestSession.tests[timeKey] || latestSession.tests[splitKey];
      if (time) {
        const splitTime = time - prevTime;
        const speed = splitTime > 0 ? (10 / splitTime) * 3.6 : 0; // km/h
        data.push({
          distance,
          time: time,
          splitTime: splitTime.toFixed(2),
          speed: parseFloat(speed.toFixed(1)),
          avgSpeed: time > 0 ? parseFloat(((distance / time) * 3.6).toFixed(1)) : 0
        });
        prevTime = time;
      }
    });

    return data;
  }, [sprintSessions]);

  // Calculate performance trend over time
  const performanceTrend = useMemo(() => {
    return sprintSessions.map(session => {
      const sprint30m = session.tests['Standing Sprint 30m'] || session.tests['Sprint 30m Split'];
      const maxSpeed = session.tests['Sprint Max Speed'] || session.tests['Sprint 30m Max Speed'];
      return {
        date: new Date(session.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        fullDate: session.date,
        time30m: sprint30m || null,
        maxSpeed: maxSpeed || null
      };
    }).filter(d => d.time30m || d.maxSpeed);
  }, [sprintSessions]);

  // Get benchmark reference lines
  const getBenchmarkLines = (testType) => {
    if (!analyticsData?.benchmarks?.[testType]) return null;
    const benchmark = analyticsData.benchmarks[testType];
    return {
      excellent: benchmark.grade_5_threshold,
      average: benchmark.grade_3_threshold,
      lowerIsBetter: benchmark.lower_is_better
    };
  };

  const sprint30mBenchmarks = getBenchmarkLines('Standing Sprint 30m');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#02b5ff]"></div>
      </div>
    );
  }

  if (sprintData.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No sprint data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Age Group & Benchmark Info Banner */}
      {analyticsData && (
        <div className={`rounded-xl p-4 border ${analyticsData.is_dob_default ? 'bg-amber-500/10 border-amber-500/30' : 'bg-[#02b5ff]/10 border-[#02b5ff]/30'}`}>
          <div className="flex items-center gap-3">
            {analyticsData.is_dob_default ? (
              <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0" />
            ) : (
              <Info className="h-5 w-5 text-[#02b5ff] flex-shrink-0" />
            )}
            <div>
              <p className={`text-sm font-medium ${analyticsData.is_dob_default ? 'text-amber-400' : 'text-[#02b5ff]'}`}>
                Age-Based Benchmarks: {analyticsData.age_group} years
                {analyticsData.is_dob_default && ' (Default DOB - Update for accurate benchmarks)'}
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">
                Current age: {analyticsData.current_age} years • Benchmarks shown as reference lines on charts
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sprint Performance Overview */}
      <div className="bg-zinc-800/30 rounded-xl p-6 border border-zinc-700/50">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="h-5 w-5 text-yellow-400" />
          <h3 className="text-lg font-bold text-white">Sprint Performance Analysis</h3>
        </div>

        {/* Key Metrics with Benchmark Comparison */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {sprintSessions.length > 0 && (
            <>
              {sprintSessions[sprintSessions.length - 1].tests['Standing Sprint 30m'] && (
                <div className="bg-zinc-900/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-[#02b5ff]">
                    {sprintSessions[sprintSessions.length - 1].tests['Standing Sprint 30m'].toFixed(2)}s
                  </div>
                  <div className="text-xs text-zinc-400">30m Time</div>
                  {sprint30mBenchmarks && (
                    <div className="text-xs mt-1">
                      {sprintSessions[sprintSessions.length - 1].tests['Standing Sprint 30m'] <= sprint30mBenchmarks.excellent ? (
                        <span className="text-emerald-400">✓ Excellent for age</span>
                      ) : sprintSessions[sprintSessions.length - 1].tests['Standing Sprint 30m'] <= sprint30mBenchmarks.average ? (
                        <span className="text-yellow-400">Average for age</span>
                      ) : (
                        <span className="text-zinc-500">Below age benchmark</span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {sprintSessions[sprintSessions.length - 1].tests['Sprint Max Speed'] && (
                <div className="bg-zinc-900/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">
                    {sprintSessions[sprintSessions.length - 1].tests['Sprint Max Speed'].toFixed(1)} km/h
                  </div>
                  <div className="text-xs text-zinc-400">Max Speed</div>
                </div>
              )}
              {sprintSessions[sprintSessions.length - 1].tests['Standing Sprint 10m'] && (
                <div className="bg-zinc-900/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-400">
                    {sprintSessions[sprintSessions.length - 1].tests['Standing Sprint 10m'].toFixed(2)}s
                  </div>
                  <div className="text-xs text-zinc-400">10m Split</div>
                </div>
              )}
              {sprintSessions[sprintSessions.length - 1].tests['Standing Sprint 20m'] && (
                <div className="bg-zinc-900/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-400">
                    {sprintSessions[sprintSessions.length - 1].tests['Standing Sprint 20m'].toFixed(2)}s
                  </div>
                  <div className="text-xs text-zinc-400">20m Split</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Speed vs Distance Chart with Benchmarks */}
        {speedDistanceData.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
              <ArrowRight className="h-4 w-4" />
              Speed Profile (Latest Session)
              {analyticsData && <span className="text-xs text-zinc-500">• Age group: {analyticsData.age_group}</span>}
            </h4>
            <div className="h-64">
              <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
                <AreaChart data={speedDistanceData}>
                  <defs>
                    <linearGradient id="speedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#02b5ff" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#02b5ff" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="distance" 
                    stroke="#9ca3af" 
                    tickFormatter={(v) => `${v}m`}
                  />
                  <YAxis 
                    stroke="#9ca3af"
                    label={{ value: 'Speed (km/h)', angle: -90, position: 'insideLeft', fill: '#9ca3af', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value, name) => {
                      if (name === 'speed') return [`${value} km/h`, 'Split Speed'];
                      if (name === 'avgSpeed') return [`${value} km/h`, 'Avg Speed'];
                      return [value, name];
                    }}
                    labelFormatter={(label) => `${label}m`}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="speed" 
                    stroke="#02b5ff" 
                    fill="url(#speedGradient)" 
                    name="Split Speed"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="avgSpeed" 
                    stroke="#10b981" 
                    name="Avg Speed"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                </AreaChart>
              </SafeResponsiveContainer>
            </div>
          </div>
        )}

        {/* Time vs Distance Chart */}
        {speedDistanceData.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
              <Timer className="h-4 w-4" />
              Time vs Distance
            </h4>
            <div className="h-64">
              <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
                <LineChart data={speedDistanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="distance" 
                    stroke="#9ca3af"
                    tickFormatter={(v) => `${v}m`}
                  />
                  <YAxis 
                    stroke="#9ca3af"
                    label={{ value: 'Time (s)', angle: -90, position: 'insideLeft', fill: '#9ca3af', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value) => [`${value}s`, 'Cumulative Time']}
                    labelFormatter={(label) => `${label}m`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="time" 
                    stroke="#f59e0b" 
                    strokeWidth={3}
                    dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }}
                    activeDot={{ r: 8, fill: '#f59e0b' }}
                  />
                </LineChart>
              </SafeResponsiveContainer>
            </div>
          </div>
        )}

        {/* Performance Trend Over Time with Age Benchmarks */}
        {performanceTrend.length > 1 && (
          <div>
            <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Performance Trend Over Time
              {sprint30mBenchmarks && (
                <span className="text-xs text-zinc-500 ml-2">
                  • Green line = Excellent ({sprint30mBenchmarks.excellent}s) • Yellow line = Average ({sprint30mBenchmarks.average}s)
                </span>
              )}
            </h4>
            <div className="h-64">
              <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
                <LineChart data={performanceTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9ca3af" />
                  <YAxis 
                    yAxisId="left"
                    stroke="#9ca3af"
                    orientation="left"
                    domain={['auto', 'auto']}
                    label={{ value: 'Time (s)', angle: -90, position: 'insideLeft', fill: '#9ca3af', fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="right"
                    stroke="#9ca3af"
                    orientation="right"
                    label={{ value: 'Speed (km/h)', angle: 90, position: 'insideRight', fill: '#9ca3af', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Legend />
                  
                  {/* Age Benchmark Reference Lines */}
                  {sprint30mBenchmarks && (
                    <>
                      <ReferenceLine 
                        yAxisId="left"
                        y={sprint30mBenchmarks.excellent} 
                        stroke="#10b981" 
                        strokeDasharray="8 4"
                        label={{ value: `Excellent (${sprint30mBenchmarks.excellent}s)`, fill: '#10b981', fontSize: 10, position: 'right' }}
                      />
                      <ReferenceLine 
                        yAxisId="left"
                        y={sprint30mBenchmarks.average} 
                        stroke="#eab308" 
                        strokeDasharray="8 4"
                        label={{ value: `Average (${sprint30mBenchmarks.average}s)`, fill: '#eab308', fontSize: 10, position: 'right' }}
                      />
                    </>
                  )}
                  
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="time30m" 
                    stroke="#02b5ff" 
                    name="30m Time"
                    strokeWidth={2}
                    dot={{ fill: '#02b5ff' }}
                    connectNulls
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="maxSpeed" 
                    stroke="#10b981" 
                    name="Max Speed"
                    strokeWidth={2}
                    dot={{ fill: '#10b981' }}
                    connectNulls
                  />
                </LineChart>
              </SafeResponsiveContainer>
            </div>
          </div>
        )}

        {/* Benchmark Legend */}
        {analyticsData?.benchmarks && Object.keys(analyticsData.benchmarks).length > 0 && (
          <div className="mt-6 pt-4 border-t border-zinc-700/50">
            <h4 className="text-xs font-bold text-zinc-400 uppercase mb-3">Age Group Benchmarks ({analyticsData.age_group} years)</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {Object.entries(analyticsData.benchmarks).map(([testName, benchmark]) => (
                <div key={testName} className="bg-zinc-900/30 rounded-lg p-3">
                  <div className="font-medium text-zinc-300 mb-1">{testName}</div>
                  <div className="text-emerald-400">Excellent: {benchmark.grade_5_threshold}{benchmark.unit}</div>
                  {benchmark.grade_3_threshold && (
                    <div className="text-yellow-400">Average: {benchmark.grade_3_threshold}{benchmark.unit}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
